import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  Braces as DataPointIcon,
  Database as DatabaseIcon,
  FileCode2 as NamedQueryIcon,
  Save as SaveIcon,
  Table2 as TableIcon
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { decodeRouteParam } from '@atbash-labs/plantview-links'
import {
  collectAdditionalProperties,
  isSelectOnlyQuery,
  useGetPlantQueryDetailQuery,
  type PlantQueryTableRef,
  type RegisterDbDatapointConnection
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Empty } from '../../components/primitives/Empty'
import { plantQueriesIndexHref } from '../../lib/plant-links'
import { computeTypeKey } from './compute-icon'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { PlantHref } from './PlantHref'
import { SaveAsDatapointDialog } from './SaveAsDatapointDialog'

/**
 * `/plant/queries/:queryId` — single database-query detail.
 *
 * Renders the query's identity, the full SQL text, the database it
 * runs against (deep-linked to its compute host), and the tables it
 * references. When the query is a read-only SELECT and its owning
 * connection posture is resolvable, offers a "Register as data point"
 * action that opens the shared `SaveAsDatapointDialog` (the A4/U1
 * `db://` registration flow), pre-filled from this query.
 */
const QueryDetailPage = (): React.ReactElement => {
  const { queryId = '' } = useParams<{ queryId: string }>()
  const decodedQueryId = decodeRouteParam(queryId)

  const { data, isLoading, isError } = useGetPlantQueryDetailQuery(decodedQueryId, {
    skip: !decodedQueryId
  })

  const [saveOpen, setSaveOpen] = React.useState(false)

  const query = data?.query
  const tables = React.useMemo(() => data?.tables ?? [], [data?.tables])
  const columns = React.useMemo(() => data?.columns ?? [], [data?.columns])
  const parameters = React.useMemo(() => data?.parameters ?? [], [data?.parameters])
  const additionalProperties = React.useMemo(
    () =>
      query
        ? collectAdditionalProperties(
            data?.kind === 'named_query' ? 'NamedQuery' : 'Tag',
            query as unknown as Record<string, unknown>
          )
        : [],
    [query, data?.kind]
  )

  const Icon = data?.kind === 'query_tag' ? DataPointIcon : NamedQueryIcon
  const registerConnection = data
    ? buildRegisterConnection(data.host, data.databaseName, data.database)
    : null
  // Only a NamedQuery (a query *definition*) can be turned into a registered
  // `db://` data point. `query_tag` rows are already registered data points /
  // query-sourced tags, so the register affordance is shown for named queries
  // only. We still *render* the button for every named query — disabling it
  // with a reason — so an unbound / non-SELECT query explains itself instead of
  // silently hiding the action.
  const showRegister = data?.kind === 'named_query'
  const registerDisabledReason: string | null = !showRegister
    ? null
    : !data?.queryText
      ? 'This named query has no stored SQL to register.'
      : !isSelectOnlyQuery(data.queryText)
        ? 'Only read-only SELECT queries can be registered as a data point.'
        : registerConnection === null
          ? "This named query isn't bound to a known database connection, so it can't be registered."
          : null
  const canRegister = showRegister && registerDisabledReason === null && registerConnection !== null

  return (
    <div className="flex flex-1 flex-col gap-card">
      <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
        <Link to={plantQueriesIndexHref()} className="hover:text-ink hover:underline">
          Queries
        </Link>
        <span className="mx-1.5 text-ink-4">/</span>
        <span className="text-ink">{query?.name ?? decodedQueryId}</span>
      </nav>

      {isError ? (
        <DetailFallback
          title="Couldn't load this query"
          hint="The query may have been deleted or the proxy is unreachable."
        />
      ) : isLoading || !data ? (
        <DetailFallback title="Loading query…" hint="" muted />
      ) : (
        <>
          <header className="flex items-start gap-4 rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
            <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
              <Icon size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h1 className="m-0 min-w-0 break-words text-2xl font-semibold text-ink">
                  {query?.name ?? decodedQueryId}
                </h1>
                <Badge variant="secondary">
                  {data.kind === 'named_query' ? 'Named query' : 'Query data point'}
                </Badge>
                {query?.query_type && <Badge variant="outline">{query.query_type}</Badge>}
              </div>
              {data.databaseName && (
                <p className="m-0 mt-2 flex items-center gap-1.5 text-sm text-ink-3">
                  <DatabaseIcon size={14} aria-hidden />
                  {data.database ? (
                    <PlantHref
                      id={data.database.id}
                      label="Database"
                      node={data.database}
                      className="hover:text-ink hover:underline"
                    >
                      {data.databaseName}
                    </PlantHref>
                  ) : (
                    data.databaseName
                  )}
                  {data.host && (
                    <>
                      <span className="text-ink-4">on</span>
                      <PlantHref
                        id={data.host.id}
                        label="Compute"
                        node={data.host}
                        className="hover:text-ink hover:underline"
                      >
                        {data.host.name}
                      </PlantHref>
                    </>
                  )}
                </p>
              )}
            </div>
          </header>

          {showRegister && (
            <div className="flex flex-col items-start gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSaveOpen(true)}
                disabled={!canRegister}
                className="self-start"
              >
                <SaveIcon size={13} className="mr-1.5" />
                Register as data point
              </Button>
              {!canRegister && registerDisabledReason && (
                <span className="text-11 text-ink-4">{registerDisabledReason}</span>
              )}
            </div>
          )}

          <section>
            <SectionHeader label="SQL" />
            {data.queryText ? (
              <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-xl border bg-bg-sunk p-4 font-mono text-13 leading-relaxed text-ink-2">
                {data.queryText}
              </pre>
            ) : (
              <EmptyCard title="No SQL recorded" hint="This query carries no stored query text." />
            )}
          </section>

          {parameters.length > 0 && (
            <section>
              <SectionHeader label="Parameters" subtitle={`${parameters.length}`} />
              <div className="flex flex-wrap gap-1.5">
                {parameters.map((param) => (
                  <Badge key={param.name} variant="outline" className="font-mono">
                    {param.name}
                    {param.type ? `: ${param.type}` : ''}
                  </Badge>
                ))}
              </div>
            </section>
          )}

          {columns.length > 0 && (
            <section>
              <SectionHeader label="Columns" subtitle={`${columns.length}`} />
              <div className="flex flex-wrap gap-1.5">
                {columns.map((col) => (
                  <Badge key={col} variant="outline" className="font-mono">
                    {col}
                  </Badge>
                ))}
              </div>
            </section>
          )}

          <TablesSection tables={tables} />

          <EntityDetailsInspector entity={data.query} additionalProperties={additionalProperties} />
        </>
      )}

      {data && registerConnection && (
        <SaveAsDatapointDialog
          open={saveOpen}
          onClose={() => setSaveOpen(false)}
          connection={registerConnection}
          query={data.queryText ?? ''}
          columns={columns}
        />
      )}
    </div>
  )
}

/**
 * Build the `db://` registration connection posture from the query's
 * owning compute host + database, mirroring the DB workbench: an
 * Ignition host reads through the WebDev relay (`transport: 'webdev'`,
 * gateway `url`), a bare database node reads direct. Returns `null`
 * when neither a gateway nor a direct URL is resolvable.
 */
function buildRegisterConnection(
  host: { url?: string; compute_type?: string } | undefined,
  databaseName: string | undefined,
  database: { url?: string; database_type?: string } | undefined
): RegisterDbDatapointConnection | null {
  if (!databaseName) return null
  // Prefer the WebDev relay through the owning Ignition gateway — the proxy
  // usually can't reach the DB directly, exactly like the DB workbench. Only a
  // genuine Ignition Compute qualifies (a `db_server` Compute's `url` is a bare
  // server endpoint, not a WebDev gateway).
  if (host?.url && computeTypeKey(host.compute_type) === 'ignition') {
    return {
      url: host.url,
      name: databaseName,
      transport: 'webdev',
      ...(database?.database_type ? { databaseType: database.database_type } : {})
    }
  }
  // Direct connection fallback (no Ignition gateway): read straight against the
  // database's own JDBC URL.
  if (database?.url) {
    return {
      url: database.url,
      name: databaseName,
      transport: 'direct',
      ...(database.database_type ? { databaseType: database.database_type } : {})
    }
  }
  return null
}

function TablesSection({ tables }: { tables: PlantQueryTableRef[] }): React.ReactElement {
  return (
    <section>
      <SectionHeader
        label="Tables referenced"
        subtitle={`${tables.length} ${tables.length === 1 ? 'table' : 'tables'}`}
      />
      {tables.length === 0 ? (
        <EmptyCard
          title="No tables referenced"
          hint="Tables are parsed from the query's SQL at ingest (named queries only)."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]">
          {tables.map((table) => (
            <div
              key={table.id}
              className="flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground shadow-sm"
            >
              <span className="grid size-9 shrink-0 place-items-center rounded-md bg-bg-sunk text-ink-3">
                <TableIcon size={16} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink">{table.name}</span>
                {table.schema && (
                  <span className="font-mono text-12 text-ink-3">{table.schema}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle?: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-baseline gap-3">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</h3>
      {subtitle && <span className="text-xs text-ink-3">{subtitle}</span>}
    </div>
  )
}

function EmptyCard({ title, hint }: { title: string; hint: string }): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-4 text-sm text-ink-3 shadow-sm">
      <div className="text-ink">{title}</div>
      {hint && <div className="mt-0.5 text-12 text-ink-3">{hint}</div>}
    </div>
  )
}

function DetailFallback({
  title,
  hint,
  muted
}: {
  title: string
  hint: string
  muted?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      {muted ? (
        <div className="text-sm text-ink-3">{title}</div>
      ) : (
        <Empty title={title} hint={hint} icon={WarningCircleIcon} />
      )}
    </div>
  )
}

export default QueryDetailPage
